import clsx from "clsx";
import { Briefcase, Eye, PencilSimple } from "phosphor-react";
import { Link } from "react-router-dom";
import { useOrganizationContext } from "../../api/current-organization/organizationContext";
import { useTalentDashboard } from "../../api/talentDashboard";
import Card from "../../components/Card";
import DashboardTableApi from "../../components/Dashboard/DashboardTableApi";
import SmallDashboardButton from "../../components/Dashboard/SmallDashboardButton";
import PageContent from "../../components/PageContent";
import Typography from "../../components/Typography";
import { useAppPaths } from "../../Routes";
import Loader from "../../components/Loader";
import ProgressIndicator from "../../components/ProgressIndicator";
import NylasCalendar from "../../components/Nylas/NylasCalendar";
import { deliveryTypeOptions, gigStatuses } from "../../models/app/gig";
import NylasAuth from "../../components/Nylas/NylasAuth";
import { formatLocationDateTimeToString } from "../../utils/dateFormatters";
import { useTalentOngoingGigs } from "../../api/talentMyOngoingGigs";
import { gigInviteStatuses } from "../../models/app/gigInvite";
import useCurrentUser from "../../auth/useCurrentUser";

const TalentDashboardPage = () => {
    const appPaths = useAppPaths();
    const { dashboardData, isLoading: isDashboardLoading } = useTalentDashboard();
    const { calendarReady, needsCalendarReconnect } = useCurrentUser();

    // Intentionally no unconditional /me refetch on mount: that runs the same calendar-health path as any other
    // /me call (including grant invalidation on hard Nylas failures). Rely on SWR revalidation / other flows
    // (e.g. Nylas callback, useNylasCalendar error reconcile) to refresh server state instead of doubling traffic here.
    const {
        gigs,
        isLoading: isGigsLoading,
        pageIndex,
        pageSize,
        totalCount,
        totalPages,
        hasPreviousPage,
        hasNextPage,
        setPageIndex
    } = useTalentOngoingGigs({
        gigStatusIds: [gigStatuses.hired, gigStatuses.pending],
        inviteStatusIds: [gigInviteStatuses.accepted, gigInviteStatuses.completed],
        orderBy: "PostedDate"
    });

    const {
        gigTerminologyPlural,
        organizationConfig: { theme },
    } = useOrganizationContext();

    
    if (isDashboardLoading || !dashboardData) {
        return <Loader />;
    }

    const isProfileComplete = dashboardData.profileHealthScore === 100;
    const shouldShowCalendar = calendarReady;

    const renderGigsTable = () => {
        if (gigs.length === 0 && !isGigsLoading) {
            return (
                <div className="flex-1 flex justify-center items-center">
                    <div className="flex flex-col items-center">
                        <img
                            src="/gigs-empty-state-icon.svg"
                            alt="No ongoing gigs icon"
                        />
                        <p className="mt-2 opacity-60">No ongoing gigs</p>
                    </div>
                </div>
            );
        }

        return (
            <DashboardTableApi
                data={gigs}
                headings={[
                    "Client name",
                    "Product",
                    "Start date",
                    "Expected duration",
                    "Delivery type",
                    "Location",
                    "",
                ]}
                isLoading={isGigsLoading}
                loadingRowCount={10}
                itemName={gigTerminologyPlural.toLowerCase()}
                paginationPlacement="both"
                pageSize={pageSize}
                pageIndex={pageIndex}
                totalCount={totalCount}
                totalPages={totalPages}
                hasPreviousPage={hasPreviousPage}
                hasNextPage={hasNextPage}
                setPageIndex={setPageIndex}
                renderRowCells={(gig) => (
                    <>
                        <td>{gig.title}</td>
                        <td>{gig.skills[0].name}</td>
                        <td>{formatLocationDateTimeToString(gig.startDate, gig.ianaId)}</td>
                        <td>{gig.expectedDurationLabel}</td>
                        <td>{deliveryTypeOptions[gig.deliveryTypeId].label}</td>
                        <td>{`${gig.location?.locality.locality}${gig.location?.locality.region ? ` (${gig.location?.locality.region})` : ""}, ${gig.location?.country}`}</td>
                        <td>
                            <Link to={appPaths.gigs.details(gig.id)}>
                                <Eye size={20} />
                            </Link>
                        </td>
                    </>
                )}
            />
        );
    };

    return (
        <PageContent>
            <Card className="space-y-6">
                {/* Profile section */}
                <Card className="bg-white flex flex-row justify-between items-center md:max-w-[33%]">
                    <div className="space-y-4 w-64">
                        <Typography component="h2" variant="title-large">Hi, {dashboardData.firstName}!</Typography>
                        <div className="flex flex-col space-y-2">
                            <div>
                                <Link to={appPaths.profile.edit} className="flex flex-row space-x-2 items-center">
                                    {isProfileComplete && <PencilSimple size={20} />}
                                    <div className="underline font-bold">{isProfileComplete ? "Edit my profile" : "Complete your profile"}</div>
                                </Link>
                            </div>
                            {!isProfileComplete && <ProgressIndicator value={dashboardData.profileHealthScore} />}
                        </div>
                    </div>
                </Card>
                {/* Dashboard buttons */}
                <div className={`space-y-6 md:space-y-0 md:flex md:space-x-6 md:child:grow ${theme === "black" ? "text-white" : ""}`}>
                    <SmallDashboardButton
                        Icon={Briefcase}
                        name={`Accepted ${gigTerminologyPlural}`}
                        count={dashboardData.ongoingGigsCount}
                        href={appPaths.gigs.ongoing}
                    />
                    <SmallDashboardButton
                        Icon={Briefcase}
                        name={`Completed ${gigTerminologyPlural}`}
                        count={dashboardData.completedGigsCount}
                        href={appPaths.gigs.completed}
                    />
                </div>
                {/* Gigs table section */}
                <div className="space-y-6 md:space-y-0 md:flex md:space-x-6 md:items-start">
                    <Card className={clsx(
                        "!bg-white",
                        "md:w-full",
                        gigs.length === 0 && !isGigsLoading && "flex flex-col min-h-[360px]"
                    )}>
                        <Typography variant="headline-small" component="h2" className="mb-6 flex-none">
                            Accepted {gigTerminologyPlural}
                        </Typography>
                        {renderGigsTable()}
                    </Card>
                </div>
                {/* Calendar section */}
                <>
                    {shouldShowCalendar && (
                        <Card className="space-y-6 mt-6 bg-white w-full">
                            <NylasCalendar />
                        </Card>
                    )}
                    {needsCalendarReconnect && (
                        <Card className="space-y-2 mt-6 bg-white w-full">
                            <Typography variant="body" component="p" className="text-orange-600 font-semibold">
                                Your calendar connection is present but could not be verified right now.
                            </Typography>
                            <Typography variant="body" component="p" className="opacity-80">
                                This may be a temporary sync issue with our calendar provider. If this persists, try reconnecting your calendar from the header or sidebar, or let us know so we can investigate.
                            </Typography>
                        </Card>
                    )}
                    <NylasAuth />
                </>
            </Card>
        </PageContent>
    );
};

export default TalentDashboardPage;